import { Injectable, inject } from '@angular/core';

import { AnalyticsService } from './analytics.service';
import { InfoModalService } from './info-modal.service';

/**
 * Builders de los modales informativos que dependen del estado de validación.
 * Centraliza copies y analítica para que tanto el flujo CU010 (cotización)
 * como el flujo de cierre del Quiz puedan mostrar exactamente la misma UI.
 */
@Injectable({ providedIn: 'root' })
export class ValidationModalsService {
  private readonly infoModalService = inject(InfoModalService);
  private readonly analyticsService = inject(AnalyticsService);

  /**
   * Modal disparador del cuestionario. Aparece cuando el usuario aún no tiene
   * el quiz aprobado (`quizStatus !== APPROVED`).
   */
  showStartQuizModal(options: {
    onStart: () => void;
    onLater: () => void;
  }): void {
    this.infoModalService.show({
      title: 'Antes de empezar...',
      message:
        'Queremos conocer cuánto sabes sobre el mundo cripto.<br>' +
        'Presta atención a cada pregunta, es importante que completes este ' +
        'cuestionario para poder continuar con tu proceso de compra.<br><br>' +
        'Si tienes dudas, escríbenos a ' +
        '<a href="mailto:criptococos@bcp.com.pe">criptococos&#64;bcp.com.pe</a>',
      buttonText: 'Empezar ahora',
      cancelText: 'Más tarde',
      closable: false,
      onConfirm: options.onStart,
      onCancel: options.onLater,
    });
  }

  /**
   * Modal "¡Vas por buen camino!" — quiz aprobado, backoffice en revisión.
   */
  showOnTheRightTrackModal(): void {
    this.infoModalService.show({
      iconSrc: 'assets/icons/ico_bell_check.svg',
      title: '¡Vas por buen camino!',
      message:
        'Ya completaste y aprobaste el cuestionario. ' +
        '<strong>Solo falta que validemos tu inscripción</strong> para ' +
        'habilitar las compras.<br><br>' +
        'Te avisaremos cuando todo esté listo.',
      buttonText: 'Entendido',
      closable: false,
      analyticsOnShow: {
        tipoElemento: 'Modal',
        zona: 'Wallet',
        subZona: 'Validación pendiente',
        seccion: 'Cuestionario aprobado - backoffice pendiente',
        cta: 'Vas por buen camino',
        pageTitle: 'Wallet - Validación pendiente',
      },
      analyticsOnConfirm: {
        tipoElemento: 'Botón',
        zona: 'Wallet',
        subZona: 'Validación pendiente',
        seccion: 'Cuestionario aprobado - backoffice pendiente',
        cta: 'Entendido',
        pageTitle: 'Wallet - Validación pendiente',
      },
    });
  }

  /**
   * Modal "¡Todo listo para empezar!" — usuario completamente validado.
   * Se muestra una sola vez al completar la transición a fullyValidated.
   */
  showReadyToBuyModal(): void {
    this.infoModalService.show({
      iconSrc: 'assets/icons/ico_bell_check.svg',
      title: '¡Todo listo para empezar!',
      message:
        'Ya puedes comprar criptomonedas y empezar a operar.<br><br>' +
        'Bienvenido al mundo cripto dentro de BCP.',
      buttonText: 'Empezar a comprar',
      closable: false,
      analyticsOnShow: {
        tipoElemento: 'Modal',
        zona: 'Wallet',
        subZona: 'Validación aprobada',
        seccion: 'Usuario completamente validado',
        cta: 'Todo listo para empezar',
        pageTitle: 'Wallet - Validación aprobada',
      },
      analyticsOnConfirm: {
        tipoElemento: 'Botón',
        zona: 'Wallet',
        subZona: 'Validación aprobada',
        seccion: 'Usuario completamente validado',
        cta: 'Empezar a comprar',
        pageTitle: 'Wallet - Validación aprobada',
      },
    });
  }

  /**
   * Modal "Gracias por tu interés" — backoffice rechazó la inscripción al
   * piloto. Se muestra cada vez que el usuario intente operar.
   */
  showRejectedModal(): void {
    this.infoModalService.show({
      iconSrc: 'assets/icons/ico_alert_new.svg',
      title: 'Gracias por tu interés',
      message:
        'Tras validar tu información, de momento no podemos habilitar tu ' +
        'acceso, en esta etapa del piloto, de igual forma puedes seguir ' +
        'explorando la wallet.<br><br>' +
        'Agradecemos que hayas sido parte del proceso.<br><br>' +
        'Si tienes alguna duda escríbenos a ' +
        '<a href="mailto:criptococos@bcp.com.pe">criptococos&#64;bcp.com.pe</a>',
      buttonText: 'Entendido',
      closable: false,
      analyticsOnShow: {
        tipoElemento: 'Modal',
        zona: 'Wallet',
        subZona: 'Validación rechazada',
        seccion: 'Backoffice rechazó la inscripción',
        cta: 'Gracias por tu interés',
        pageTitle: 'Wallet - Validación rechazada',
      },
      analyticsOnConfirm: {
        tipoElemento: 'Botón',
        zona: 'Wallet',
        subZona: 'Validación rechazada',
        seccion: 'Backoffice rechazó la inscripción',
        cta: 'Entendido',
        pageTitle: 'Wallet - Validación rechazada',
      },
    });
  }

  /**
   * Helper para tracking del evento "Más tarde" del modal disparador.
   */
  trackLaterAction(context: { subZona: string; moneda: string }): void {
    this.analyticsService.trackVirtualEvent({
      tipoElemento: 'Botón',
      zona: 'Wallet',
      subZona: context.subZona,
      seccion: 'Validación pendiente',
      subSeccion: context.moneda,
      cta: 'Más tarde',
      pageTitle: `Wallet - ${context.subZona} - Validación pendiente`,
    });
  }

  /**
   * Helper para tracking del evento "Empezar ahora" del modal disparador.
   */
  trackStartAction(context: { subZona: string; moneda: string }): void {
    this.analyticsService.trackVirtualEvent({
      tipoElemento: 'Botón',
      zona: 'Wallet',
      subZona: context.subZona,
      seccion: 'Validación pendiente',
      subSeccion: context.moneda,
      cta: 'Empezar ahora',
      pageTitle: `Wallet - ${context.subZona} - Validación pendiente`,
    });
  }
}
